/*
 * PARCOS wishlist.
 *
 * parcos.com keeps the wishlist in Magento, against the signed-in customer:
 * `PUT V1/wishlist/{sku}` to add, `V1/wishlist/items` to read back. Shopify has
 * no equivalent a theme can reach. The Storefront API's customer mutations all
 * need a `customerAccessToken`, and this store runs NEW customer accounts,
 * which have no password — so `customerAccessTokenCreate` cannot be called at
 * all and no customer metafield can be written from the storefront. The Admin
 * API can write one, but only from a server; see the theme README
 * (**Wishlist → Making it follow the customer**) for what that would take.
 *
 * So the list is held in localStorage, KEYED BY CUSTOMER. `owner` is
 * `c:<customer.id>` while signed in and `guest` while not, which means two
 * people sharing a browser never see each other's list, and a guest who saves
 * something and then signs in keeps it — the guest list is merged into the
 * customer's on the first signed-in page view and then dropped. What it does
 * NOT do is follow a customer to another device; nothing in a theme can.
 *
 * Three jobs:
 *
 *   1. State.   Read/write/merge the lists, and keep every heart on the page
 *               and the header count in step with them (including across tabs).
 *   2. Toggle.  One delegated click handler for every `[data-wishlist-toggle]`,
 *               so hearts injected later — quick-buy modals, the wishlist page
 *               itself — work without re-binding.
 *   3. Page.    /pages/wishlist renders from storage: each saved handle is
 *               fetched as a card fragment (`?view=parcos-wishlist-card`), so
 *               prices, stock and images come from Shopify at read time rather
 *               than from whatever was true when the item was saved.
 *
 * No framework and no build step — a theme asset, loaded sitewide because the
 * header count is.
 */
(function () {
  'use strict';

  var CONFIG = window.parcosWishlistConfig || {};
  var STORE_KEY = 'parcos:wishlist';
  var ROOT = CONFIG.rootUrl || '/';
  var OWNER = CONFIG.customerId ? 'c:' + CONFIG.customerId : 'guest';

  var listeners = [];

  /* ---------------------------------------------------------------- helpers */

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function url(path) {
    return ROOT.replace(/\/$/, '') + path;
  }

  /** The account toast, reused verbatim so the two pages sound the same. */
  function toast(message) {
    var host = $('.parcos-toast-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'parcos-toast-host';
      host.setAttribute('role', 'status');
      host.setAttribute('aria-live', 'polite');
      document.body.appendChild(host);
    }

    var node = document.createElement('div');
    node.className = 'parcos-toast';
    node.textContent = message;
    host.appendChild(node);

    window.setTimeout(function () {
      node.classList.add('is-leaving');
      window.setTimeout(function () {
        if (node.parentNode) node.parentNode.removeChild(node);
      }, 300);
    }, 2600);
  }

  /* ------------------------------------------------------------------ state */

  /*
   * Every read and write is wrapped: localStorage throws outright in Safari's
   * private mode and when a viewer has blocked site data, and a wishlist is
   * never worth taking the rest of the page down for. The failure mode is a
   * heart that does not stay filled, which is visible and harmless.
   */
  function readLists() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.lists) return {};
      return parsed.lists;
    } catch (error) {
      return {};
    }
  }

  function writeLists(lists) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify({ v: 1, lists: lists }));
    } catch (error) {
      /* quota, private mode, blocked site data — nothing to recover */
    }
  }

  /**
   * Anything saved while signed out belongs to whoever signs in next on this
   * browser. Runs once per page load, before the first read, and only when the
   * guest list actually has something in it.
   */
  function mergeGuestList(lists) {
    if (OWNER === 'guest') return false;

    var guest = lists.guest || [];
    if (!guest.length) return false;

    var mine = lists[OWNER] || [];
    var seen = {};
    mine.forEach(function (item) {
      seen[item.handle] = true;
    });
    guest.forEach(function (item) {
      if (!seen[item.handle]) mine.unshift(item);
    });

    lists[OWNER] = mine;
    delete lists.guest;
    return true;
  }

  function items() {
    var lists = readLists();
    if (mergeGuestList(lists)) writeLists(lists);
    return lists[OWNER] || [];
  }

  function save(next) {
    var lists = readLists();
    lists[OWNER] = next;
    writeLists(lists);
    broadcast();
  }

  function count() {
    return items().length;
  }

  function has(handle) {
    return items().some(function (item) {
      return item.handle === handle;
    });
  }

  function add(handle, id) {
    if (!handle || has(handle)) return false;
    var next = items().slice();
    // Newest first: the wishlist page reads top-down, so the thing just saved
    // is the thing the viewer sees when they follow the header heart.
    next.unshift({ handle: handle, id: id || null, at: Date.now() });
    save(next);
    return true;
  }

  function remove(handle) {
    var next = items().filter(function (item) {
      return item.handle !== handle;
    });
    save(next);
    return true;
  }

  function toggle(handle, id) {
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle, id);
    return true;
  }

  function broadcast() {
    listeners.forEach(function (fn) {
      try {
        fn();
      } catch (error) {
        /* one bad listener must not stop the others */
      }
    });
  }

  /* -------------------------------------------------------------------- DOM */

  /**
   * Hearts render un-pressed in Liquid and are filled here. They have to be:
   * Shopify caches and serves the same HTML to every visitor, so saved state
   * can never be baked into the markup.
   */
  function syncToggles(scope) {
    var saved = {};
    items().forEach(function (item) {
      saved[item.handle] = true;
    });

    $$('[data-wishlist-toggle]', scope).forEach(function (button) {
      var on = !!saved[button.getAttribute('data-wishlist-handle')];
      button.classList.toggle('is-wishlisted', on);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      button.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
    });
  }

  function syncCount() {
    var total = count();
    $$('[data-wishlist-count]').forEach(function (node) {
      node.textContent = total;
      node.hidden = total === 0;
    });
  }

  /*
   * Delegated, not bound per button: cards arrive after load from the quick-buy
   * modal, the wishlist page and any section rendered over the Section
   * Rendering API, and all of their hearts have to work without re-binding.
   */
  function onClick(event) {
    var button = event.target.closest && event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    var handle = button.getAttribute('data-wishlist-handle');
    if (!handle) return;

    var added = toggle(handle, button.getAttribute('data-wishlist-id'));
    toast(added ? 'Added to Wishlist' : 'Removed from Wishlist');
  }

  /* ------------------------------------------------------------------- page */

  var page = null;

  /**
   * One fetch per saved item, in parallel, against a bare Liquid template —
   * `templates/product.parcos-wishlist-card.liquid`, which is `{% layout none %}`
   * and renders the card and nothing else.
   *
   * Storage holds handles, never prices: a card built from what was true at
   * save time would go stale the first time a price or an image changed. A
   * handle that no longer resolves (product deleted, unpublished, renamed) is
   * dropped from the list rather than rendered as a hole.
   */
  function fetchCard(item) {
    return window
      .fetch(url('/products/' + encodeURIComponent(item.handle) + '?view=parcos-wishlist-card'), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      .then(function (response) {
        if (!response.ok) return null;
        return response.text();
      })
      .then(function (html) {
        return html && html.trim() ? html : null;
      })
      .catch(function () {
        // A network failure is not a deleted product: keep the item and show
        // nothing for it this time round.
        return '';
      });
  }

  function renderPage() {
    if (!page) return;

    var saved = items();
    if (!saved.length) {
      page.grid.innerHTML = '';
      page.root.setAttribute('data-state', 'empty');
      page.countLine.textContent = '';
      return;
    }

    page.root.setAttribute('data-state', 'loading');

    Promise.all(saved.map(fetchCard)).then(function (cards) {
      var stale = [];
      var html = '';

      cards.forEach(function (card, index) {
        if (card === null) {
          stale.push(saved[index].handle);
          return;
        }
        html += card;
      });

      if (stale.length) {
        save(
          items().filter(function (item) {
            return stale.indexOf(item.handle) === -1;
          })
        );
      }

      var remaining = items();
      if (!remaining.length) {
        page.grid.innerHTML = '';
        page.root.setAttribute('data-state', 'empty');
        page.countLine.textContent = '';
        return;
      }

      page.grid.innerHTML = html;
      page.root.setAttribute('data-state', 'ready');
      page.countLine.textContent =
        remaining.length + ' item' + (remaining.length === 1 ? '' : 's') + ' in your wishlist';
      syncToggles(page.grid);
    });
  }

  /**
   * Removing on the wishlist page takes the card out rather than re-rendering:
   * a full re-render would re-fetch every remaining product to delete one, and
   * would scroll-jump the grid under the pointer.
   */
  function onPageChange() {
    if (!page) return;

    var saved = {};
    items().forEach(function (item) {
      saved[item.handle] = true;
    });

    var rendered = $$('[data-wishlist-card]', page.grid);
    var kept = 0;

    rendered.forEach(function (card) {
      if (saved[card.getAttribute('data-wishlist-card')]) {
        kept += 1;
      } else {
        card.remove();
      }
    });

    // Something was added from another tab — that needs the real render.
    if (Object.keys(saved).length > kept) {
      renderPage();
      return;
    }

    if (!kept) {
      page.root.setAttribute('data-state', 'empty');
      page.countLine.textContent = '';
      return;
    }

    page.countLine.textContent = kept + ' item' + (kept === 1 ? '' : 's') + ' in your wishlist';
  }

  /**
   * Hearts arrive after load in three ways that have nothing in common:
   * `product-rerender` replaces the whole `buy-buttons` block — the PDP heart
   * with it — on every variant change; `quick-buy-modal` fetches a product page
   * and lifts a fragment out of it; and the wishlist page injects its own cards.
   * All three produce un-pressed markup, so rather than hook each one, any
   * added subtree carrying a toggle re-fills from storage.
   *
   * Coalesced to one pass per frame: a rerender can add dozens of nodes in a
   * single mutation batch.
   */
  function watchForToggles() {
    if (!window.MutationObserver) return;

    var queued = false;

    var observer = new MutationObserver(function (records) {
      if (queued) return;

      var relevant = records.some(function (record) {
        return Array.prototype.some.call(record.addedNodes, function (node) {
          if (node.nodeType !== 1) return false;
          return (
            (node.matches && node.matches('[data-wishlist-toggle]')) ||
            (node.querySelector && node.querySelector('[data-wishlist-toggle]'))
          );
        });
      });
      if (!relevant) return;

      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        syncToggles(document);
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------- boot */

  function init() {
    var root = $('[data-parcos-wishlist-page]');
    if (root) {
      page = {
        root: root,
        grid: $('[data-wishlist-grid]', root),
        countLine: $('[data-wishlist-count-line]', root),
      };
    }

    document.addEventListener('click', onClick);

    listeners.push(function () {
      syncToggles(document);
      syncCount();
    });
    if (page) listeners.push(onPageChange);

    // Another tab wrote the list. `storage` only fires on OTHER tabs, so this
    // cannot loop with our own writes.
    window.addEventListener('storage', function (event) {
      if (event.key && event.key !== STORE_KEY) return;
      syncToggles(document);
      syncCount();
      if (page) renderPage();
    });

    // Coming back to a page from the bfcache restores the DOM as it was, which
    // may be older than a list another tab has since changed.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      syncToggles(document);
      syncCount();
      if (page) renderPage();
    });

    watchForToggles();

    syncToggles(document);
    syncCount();
    if (page) renderPage();
  }

  window.parcosWishlist = {
    items: items,
    count: count,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    refresh: function (scope) {
      syncToggles(scope || document);
      syncCount();
    },
    subscribe: function (fn) {
      listeners.push(fn);
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
